import { useState, useRef, useEffect } from 'react';
import {
    Card,
    CardHeader,
    CardMedia,
    CardContent,
    CardActions,
    Avatar,
    IconButton,
    Typography,
    Box,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    Button,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    TextField,
    Grid
} from '@mui/material';
import {
    Favorite as FavoriteIcon,
    FavoriteBorder as FavoriteBorderIcon,
    Comment as CommentIcon,
    Share as ShareIcon,
    PlayArrow as PlayArrowIcon,
    PushPin as PushPinIcon,
    PushPinOutlined as PushPinOutlinedIcon,
    Delete as DeleteIcon,
    ShoppingBag as ShoppingBagIcon,
    AddShoppingCart as AddShoppingCartIcon,
    ShoppingCart as ShoppingCartIcon
} from '@mui/icons-material';
import CommentsModal from './CommentsModal';
import { formatTimeAgo } from '../utils/timeUtils';
import contentService from '../services/contentService';
import cartService from '../services/cartService';
import { toast } from 'react-toastify';
import { useCart } from '../context/CartContext';
import '../styles/ProtectedRoutes.css';

const ContentCard = ({ content, onLike, onShare, currentUser, onPinToggle, onUploaderClick, onDelete }) => {
    const [commentsModalOpen, setCommentsModalOpen] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [productsModalOpen, setProductsModalOpen] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [imageError, setImageError] = useState(false);
    const [imageLoaded, setImageLoaded] = useState(false);
    const [captionExpanded, setCaptionExpanded] = useState(false);
    const [videoPlayCount, setVideoPlayCount] = useState(0);
    const [showReplayButton, setShowReplayButton] = useState(false);
    const [isPinned, setIsPinned] = useState(content.IsPinned === 1);
    const [pinning, setPinning] = useState(false);
    const [addToCartDialogOpen, setAddToCartDialogOpen] = useState(false);
    const [selectedItems, setSelectedItems] = useState({});
    const [isAddingToCart, setIsAddingToCart] = useState(false);
    const [commentCount, setCommentCount] = useState(parseInt(content.CommentCount) || 0);
    const videoRef = useRef(null);
    const { refreshCart } = useCart();

    const isLiked = content.IsLikedByUser === 1;
    const isVideo = content.ContentType === 'video';
    const isAudio = content.ContentType === 'audio';

    // Check if current user is the owner of this post
    const isOwnPost = currentUser && content.UploadedBy === currentUser.userId;
    const isAdmin = currentUser && currentUser.role === 'Admin';
    const canDelete = isOwnPost || isAdmin;

    // If any tagged product has size variants, force the size-picker flow
    // instead of "Add All to Cart" (which would add with size: null).
    const productsRequireSize = content.AttachedProducts?.some(
        (p) => Array.isArray(p.SizeDetails) && p.SizeDetails.length > 0
    );

    // Update pin state when content changes
    useEffect(() => {
        setIsPinned(content.IsPinned === 1);
    }, [content.IsPinned]);

    const handleVideoEnded = () => {
        const newCount = videoPlayCount + 1;
        setVideoPlayCount(newCount);

        if (newCount < 2) {
            // Play again for the second time
            if (videoRef.current) {
                videoRef.current.play();
            }
        } else {
            // After 2 plays, show replay button
            setShowReplayButton(true);
        }
    };

    const handleReplay = () => {
        setVideoPlayCount(0);
        setShowReplayButton(false);
        if (videoRef.current) {
            videoRef.current.play();
        }
    };

    // Get the backend server URL for media files
    const getMediaUrl = (filePath) => {
        if (!filePath) return '';
        if (filePath.startsWith('http')) return filePath;
        const backendUrl = process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:5000';
        return `${backendUrl}${filePath}`;
    };

    const handleLikeClick = () => {
        onLike(content.ContentId);
    };

    const handleCommentsClick = () => {
        setCommentsModalOpen(true);
    };

    const handleShareClick = () => {
        onShare(content.ContentId);
    };

    const handlePinClick = async () => {
        if (!currentUser || pinning) return;

        setPinning(true);
        try {
            if (isPinned) {
                await contentService.unpinPost(content.ContentId);
                setIsPinned(false);
            } else {
                await contentService.pinPost(content.ContentId);
                setIsPinned(true);
            }
            if (onPinToggle) {
                onPinToggle(content.ContentId, !isPinned);
            }
        } catch (error) {
            toast.error(error.message || 'Failed to pin/unpin post');
        } finally {
            setPinning(false);
        }
    };

    const handleDeleteClick = () => {
        setDeleteDialogOpen(true);
    };

    const handleDeleteConfirm = async () => {
        setDeleting(true);
        try {
            const response = await contentService.deletePost(content.ContentId);
            if (response.success) {
                toast.success('Post deleted successfully');
                setDeleteDialogOpen(false);
                if (onDelete) {
                    onDelete(content.ContentId);
                }
            } else {
                toast.error(response.message || 'Failed to delete post');
            }
        } catch (error) {
            toast.error(error.message || 'Failed to delete post');
        } finally {
            setDeleting(false);
        }
    };

    const handleDeleteCancel = () => {
        setDeleteDialogOpen(false);
    };

    const handleOpenAddToCartDialog = () => {
        // Pre-populate one empty size row per product so the size dropdown
        // is immediately visible when the dialog opens.
        const initialSelections = {};
        content.AttachedProducts?.forEach(product => {
            const hasSizes = Array.isArray(product.SizeDetails) && product.SizeDetails.length > 0;
            initialSelections[product.ProductCode] = hasSizes
                ? [{ size: '', quantity: 1 }]
                : [];
        });
        setSelectedItems(initialSelections);
        setAddToCartDialogOpen(true);
    };

    const handleAddSizeToProduct = (productCode) => {
        setSelectedItems(prev => ({
            ...prev,
            [productCode]: [...(prev[productCode] || []), { size: '', quantity: 1 }]
        }));
    };

    const handleRemoveSizeFromProduct = (productCode, index) => {
        setSelectedItems(prev => ({
            ...prev,
            [productCode]: prev[productCode].filter((_, i) => i !== index)
        }));
    };

    const handleSizeChange = (productCode, index, size) => {
        setSelectedItems(prev => ({
            ...prev,
            [productCode]: prev[productCode].map((item, i) =>
                i === index ? { ...item, size } : item
            )
        }));
    };

    const handleQuantityChange = (productCode, index, quantity, maxStock) => {
        const qty = parseInt(quantity) || 1;
        const validQty = Math.max(1, Math.min(qty, maxStock)); // Enforce min 1 and max stock

        setSelectedItems(prev => ({
            ...prev,
            [productCode]: prev[productCode].map((item, i) =>
                i === index ? { ...item, quantity: validQty } : item
            )
        }));

        // Show warning if user tried to exceed stock
        if (qty > maxStock) {
            toast.warning(`Only ${maxStock} units available for this size`);
        }
    };

    const handleAddSelectedToCart = async () => {
        setIsAddingToCart(true);
        try {
            const itemsToAdd = [];

            // Collect all selected items
            Object.entries(selectedItems).forEach(([productCode, selections]) => {
                selections.forEach(selection => {
                    if (selection.size) {
                        itemsToAdd.push({
                            productCode,
                            size: selection.size,
                            quantity: selection.quantity
                        });
                    }
                });
            });

            if (itemsToAdd.length === 0) {
                toast.error('Please select at least one size');
                return;
            }

            // Add each item to cart
            for (const item of itemsToAdd) {
                await cartService.addToCart(item.productCode, item.quantity, item.size);
            }

            toast.success(`Added ${itemsToAdd.length} item(s) to cart`);
            refreshCart();
            setAddToCartDialogOpen(false);
        } catch (error) {
            console.error('Add to cart error:', error);
            toast.error(error.message || 'Failed to add items to cart');
        } finally {
            setIsAddingToCart(false);
        }
    };

    const handleAddAllToCart = async () => {
        if (!content.AttachedProducts || content.AttachedProducts.length === 0) {
            return;
        }

        try {
            await cartService.addMultipleToCart(content.AttachedProducts);
            const productCount = content.AttachedProducts.length;
            toast.success(`Added ${productCount} product${productCount > 1 ? 's' : ''} to cart`);
            refreshCart();  // ← missing this line
            if (productsModalOpen) {
                setProductsModalOpen(false);
            }
        } catch (error) {
            toast.error(error.message || 'Failed to add products to cart');
        }
    };

    const handleCommentAdded = () => {
        setCommentCount(prev => prev + 1);
    };

    const handleImageError = (e) => {
        if (!imageError) {
            setImageError(true);
            // Only set placeholder once to prevent infinite loop
            e.target.src = '/placeholder-image.jpg';
        }
    };

    const handleImageLoad = () => {
        setImageLoaded(true);
    };

    const formatDate = (dateString) => {
        return formatTimeAgo(dateString);
    };

    return (
        <Card elevation={2} className="glass-card" sx={{
            width: '100%',
            mb: 2,
            overflow: 'hidden'
        }}>
            {/* Header */}
            <CardHeader
                avatar={
                    <Avatar
                        className="glass-avatar"
                        sx={{
                            bgcolor: 'primary.main',
                            cursor: onUploaderClick ? 'pointer' : 'default'
                        }}
                        onClick={() => onUploaderClick && onUploaderClick(content.UploadedBy)}
                    >
                        {content.UploaderName ? content.UploaderName.charAt(0).toUpperCase() : 'A'}
                    </Avatar>
                }
                title={
                    <Typography
                        variant="subtitle1"
                        fontWeight="bold"
                        className="glass-text-primary"
                        sx={{
                            cursor: onUploaderClick ? 'pointer' : 'default',
                            '&:hover': onUploaderClick ? { color: '#bc965e' } : {}
                        }}
                        onClick={() => onUploaderClick && onUploaderClick(content.UploadedBy)}
                    >
                        {content.UploaderName || 'Admin'}
                    </Typography>
                }
                subheader={
                    <Typography variant="body2" className="glass-text-secondary">
                        {formatDate(content.CreatedAt)}
                    </Typography>
                }
            />

            {/* Content Media */}
            <Box sx={{ position: 'relative' }}>
                {isVideo ? (
                    <Box sx={{ position: 'relative' }}>
                        <CardMedia
                            component="video"
                            ref={videoRef}
                            autoPlay
                            muted
                            playsInline
                            poster={content.ThumbnailPath ? getMediaUrl(content.ThumbnailPath) : undefined}
                            src={getMediaUrl(content.FilePath)}
                            onEnded={handleVideoEnded}
                            sx={{
                                width: '100%',
                                height: 400,
                                objectFit: 'cover'
                            }}
                        />
                        {showReplayButton && (
                            <Box
                                onClick={handleReplay}
                                sx={{
                                    position: 'absolute',
                                    top: '50%',
                                    left: '50%',
                                    transform: 'translate(-50%, -50%)',
                                    cursor: 'pointer',
                                    backgroundColor: 'rgba(0, 0, 0, 0.7)',
                                    borderRadius: '50%',
                                    width: 80,
                                    height: 80,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    transition: 'all 0.3s ease',
                                    '&:hover': {
                                        backgroundColor: 'rgba(0, 0, 0, 0.9)',
                                        transform: 'translate(-50%, -50%) scale(1.1)'
                                    }
                                }}
                            >
                                <PlayArrowIcon sx={{ fontSize: 50, color: 'white' }} />
                            </Box>
                        )}
                    </Box>
                ) : isAudio ? (
                    <Box sx={{
                        height: 200,
                        backgroundColor: 'rgba(188, 150, 94, 0.1)',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        p: 3
                    }}>
                        <PlayArrowIcon sx={{ fontSize: 80, color: '#bc965e', mb: 2 }} />
                        <Typography variant="h6" className="glass-text-primary" gutterBottom>
                            Audio Content
                        </Typography>
                        <audio controls style={{ width: '100%', maxWidth: '400px' }}>
                            <source src={getMediaUrl(content.FilePath)} />
                            Your browser does not support the audio element.
                        </audio>
                    </Box>
                ) : (
                    <Box sx={{ position: 'relative', height: 400, backgroundColor: '#1a1a1a' }}>
                        {!imageLoaded && !imageError && (
                            <Box
                                sx={{
                                    position: 'absolute',
                                    top: '50%',
                                    left: '50%',
                                    transform: 'translate(-50%, -50%)',
                                    zIndex: 2
                                }}
                            >
                                <Typography variant="body2" className="glass-text-secondary">
                                    Loading image...
                                </Typography>
                            </Box>
                        )}
                        <CardMedia
                            component="img"
                            height="400"
                            image={getMediaUrl(content.FilePath)}
                            alt={content.Title || 'Content image'}
                            sx={{
                                width: '100%',
                                objectFit: 'cover',
                                opacity: imageLoaded ? 1 : 0,
                                transition: 'opacity 0.3s ease-in-out'
                            }}
                            onLoad={handleImageLoad}
                            onError={handleImageError}
                        />
                        {imageError && (
                            <Box
                                sx={{
                                    position: 'absolute',
                                    top: '50%',
                                    left: '50%',
                                    transform: 'translate(-50%, -50%)',
                                    textAlign: 'center',
                                    zIndex: 2
                                }}
                            >
                                <Typography variant="body2" className="glass-text-secondary">
                                    Image not available
                                </Typography>
                            </Box>
                        )}
                    </Box>
                )}

                {/* Caption Overlay */}
                {(content.Title || content.Caption || (content.Tags && content.Tags.length > 0)) && !isAudio && (
                    <Box
                        sx={{
                            position: 'absolute',
                            bottom: 0,
                            left: 0,
                            right: 0,
                            background: 'linear-gradient(to top, rgba(0, 0, 0, 0.95) 0%, rgba(0, 0, 0, 0.85) 50%, rgba(0, 0, 0, 0.6) 100%)',
                            p: 2,
                            color: 'white',
                            maxHeight: captionExpanded ? '80%' : '120px',
                            overflowY: captionExpanded ? 'auto' : 'hidden',
                            transition: 'max-height 0.3s ease-in-out',
                            '&::-webkit-scrollbar': {
                                width: '6px'
                            },
                            '&::-webkit-scrollbar-track': {
                                background: 'rgba(255, 255, 255, 0.1)',
                                borderRadius: '3px'
                            },
                            '&::-webkit-scrollbar-thumb': {
                                background: 'rgba(188, 150, 94, 0.6)',
                                borderRadius: '3px',
                                '&:hover': {
                                    background: 'rgba(188, 150, 94, 0.8)'
                                }
                            }
                        }}
                    >
                        {content.Title && (
                            <Typography
                                variant="subtitle1"
                                fontWeight="bold"
                                sx={{
                                    mb: 0.5,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    display: captionExpanded ? 'block' : '-webkit-box',
                                    WebkitLineClamp: captionExpanded ? 'unset' : 1,
                                    WebkitBoxOrient: 'vertical'
                                }}
                            >
                                {content.Title}
                            </Typography>
                        )}
                        {content.Caption && (
                            <>
                                <Typography
                                    variant="body2"
                                    sx={{
                                        fontStyle: 'italic',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        display: captionExpanded ? 'block' : '-webkit-box',
                                        WebkitLineClamp: captionExpanded ? 'unset' : 2,
                                        WebkitBoxOrient: 'vertical',
                                        mb: 0.5
                                    }}
                                >
                                    {content.Caption}
                                </Typography>
                                {!captionExpanded && (
                                    <Typography
                                        variant="caption"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setCaptionExpanded(true);
                                        }}
                                        sx={{
                                            cursor: 'pointer',
                                            color: '#bc965e',
                                            fontWeight: 'bold',
                                            textDecoration: 'underline',
                                            display: 'block',
                                            mb: 2.5,
                                            '&:hover': {
                                                color: '#d4a76a'
                                            }
                                        }}
                                    >
                                        view more
                                    </Typography>
                                )}
                            </>
                        )}
                        {/* Tags in overlay */}
                        {content.Tags && content.Tags.length > 0 && (
                            <Box sx={{ mt: 1, mb: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                {content.Tags.slice(0, captionExpanded ? content.Tags.length : 3).map((tag, index) => (
                                    <Chip
                                        key={index}
                                        label={`#${tag}`}
                                        size="small"
                                        sx={{
                                            backgroundColor: 'rgba(188, 150, 94, 0.3)',
                                            color: 'white',
                                            fontSize: '0.7rem',
                                            height: '20px',
                                            '& .MuiChip-label': {
                                                px: 1
                                            }
                                        }}
                                    />
                                ))}
                                {!captionExpanded && content.Tags.length > 3 && (
                                    <Typography variant="caption" sx={{ color: '#bc965e', alignSelf: 'center' }}>
                                        +{content.Tags.length - 3} more
                                    </Typography>
                                )}
                            </Box>
                        )}
                        {captionExpanded && (
                            <Typography
                                variant="caption"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setCaptionExpanded(false);
                                }}
                                sx={{
                                    cursor: 'pointer',
                                    color: '#bc965e',
                                    fontWeight: 'bold',
                                    textDecoration: 'underline',
                                    display: 'block',
                                    mt: 0.5,
                                    '&:hover': {
                                        color: '#d4a76a'
                                    }
                                }}
                            >
                                view less
                            </Typography>
                        )}
                    </Box>
                )}
            </Box>

            {/* Action Buttons */}
            <CardActions disableSpacing sx={{ px: 2, py: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <IconButton
                        onClick={handleLikeClick}
                        disabled={!currentUser}
                        color={isLiked ? 'error' : 'default'}
                        size="small"
                        className="glass-icon-button"
                        sx={{ color: isLiked ? '#ff6b6b' : '#bc965e' }}
                    >
                        {isLiked ? <FavoriteIcon /> : <FavoriteBorderIcon />}
                    </IconButton>
                    <Typography variant="body2" className="glass-text-secondary">
                        {content.LikeCount || 0}
                    </Typography>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 2 }}>
                    <IconButton
                        onClick={handleCommentsClick}
                        size="small"
                        className="glass-icon-button"
                        sx={{ color: '#bc965e' }}
                    >
                        <CommentIcon />
                    </IconButton>
                    <Typography variant="body2" className="glass-text-secondary">
                        {commentCount}
                    </Typography>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 2 }}>
                    <IconButton
                        onClick={handleShareClick}
                        disabled={!currentUser || currentUser.role !== 'Customer'}
                        size="small"
                        className="glass-icon-button"
                        sx={{ color: '#bc965e' }}
                    >
                        <ShareIcon />
                    </IconButton>
                    <Typography variant="body2" className="glass-text-secondary">
                        {content.ShareCount || 0}
                    </Typography>
                </Box>

                {/* Products Button */}
                {content.AttachedProducts && content.AttachedProducts.length > 0 && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 2 }}>
                        <IconButton
                            onClick={() => setProductsModalOpen(true)}
                            size="small"
                            className="glass-icon-button"
                            sx={{ color: '#bc965e' }}
                            title="View tagged products"
                        >
                            <ShoppingBagIcon />
                        </IconButton>
                        <Typography variant="body2" className="glass-text-secondary">
                            {content.AttachedProducts.length}
                        </Typography>
                    </Box>
                )}

                {/* Add to Cart Button - Only for customers viewing other users' posts */}

                {(!currentUser || currentUser.role === 'Customer') && !isOwnPost && (
                    <IconButton
                        onClick={() => {
                            setProductsModalOpen(false);
                            handleOpenAddToCartDialog();
                        }}
                        title="Add to Cart"
                        sx={{
                            mr: 'auto',
                            ml: 1,
                    
                            color: '#bc965e',
                            '&:hover': {
                                backgroundColor: 'rgba(78, 62, 38, 0.25)'
                            }
                        }}
                    >
                        <AddShoppingCartIcon />
                    </IconButton>
                )}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 'auto' }}>
                    {/* Only show pin button if user owns the post */}
                    {isOwnPost && (
                        <IconButton
                            onClick={handlePinClick}
                            disabled={!currentUser || pinning}
                            size="small"
                            className="glass-icon-button"
                            sx={{ color: isPinned ? '#ffd700' : '#bc965e' }}
                            title={isPinned ? 'Unpin post' : 'Pin post'}
                        >
                            {isPinned ? <PushPinIcon /> : <PushPinOutlinedIcon />}
                        </IconButton>
                    )}

                    {/* Show delete button if user can delete (own post or admin) */}
                    {canDelete && (
                        <IconButton
                            onClick={handleDeleteClick}
                            disabled={deleting}
                            size="small"
                            className="glass-icon-button"
                            sx={{ color: '#ff6b6b' }}
                            title="Delete post"
                        >
                            <DeleteIcon />
                        </IconButton>
                    )}
                </Box>
            </CardActions>

            {/* Delete Confirmation Dialog */}
            <Dialog
                open={deleteDialogOpen}
                onClose={handleDeleteCancel}
                PaperProps={{
                    sx: {
                        background: 'rgba(30, 30, 30, 0.95)',
                        backdropFilter: 'blur(10px)',
                        border: '1px solid rgba(188, 150, 94, 0.2)',
                        borderRadius: 2
                    }
                }}
            >
                <DialogTitle sx={{ color: 'white' }}>
                    Delete Post?
                </DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                        Are you sure you want to delete this post? This action cannot be undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button
                        onClick={handleDeleteCancel}
                        disabled={deleting}
                        sx={{
                            color: '#bc965e',
                            '&:hover': {
                                backgroundColor: 'rgba(188, 150, 94, 0.1)'
                            }
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleDeleteConfirm}
                        disabled={deleting}
                        variant="contained"
                        sx={{
                            backgroundColor: '#ff6b6b',
                            '&:hover': {
                                backgroundColor: '#ff5252'
                            }
                        }}
                    >
                        {deleting ? 'Deleting...' : 'Delete'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Content Details */}
            <CardContent sx={{ pt: 0 }}>
            </CardContent>

            {/* Products Modal */}
            <Dialog
                open={productsModalOpen}
                onClose={() => setProductsModalOpen(false)}
                maxWidth="sm"
                fullWidth
                slotProps={{
                    paper: {
                        sx: {
                            background: 'rgba(30, 30, 30, 0.95)',
                            backdropFilter: 'blur(10px)',
                            border: '1px solid rgba(188, 150, 94, 0.2)',
                            borderRadius: 2
                        }
                    }
                }}
            >
                <DialogTitle sx={{ color: 'white', borderBottom: '1px solid rgba(188, 150, 94, 0.2)' }}>
                    Tagged Products
                </DialogTitle>
                <DialogContent sx={{ pt: 2 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {content.AttachedProducts && content.AttachedProducts.map((product) => (
                            <Box
                                key={product.ProductCode}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 2,
                                    p: 2,
                                    borderRadius: 2,
                                    backgroundColor: 'rgba(188, 150, 94, 0.1)',
                                    border: '1px solid rgba(188, 150, 94, 0.3)',
                                    transition: 'all 0.3s ease',
                                    '&:hover': {
                                        backgroundColor: 'rgba(188, 150, 94, 0.2)',
                                        transform: 'translateY(-2px)',
                                        boxShadow: '0 4px 12px rgba(188, 150, 94, 0.2)'
                                    }
                                }}
                            >
                                <Avatar
                                    src={product.Photo}
                                    alt={product.ProductName}
                                    sx={{ width: 60, height: 60 }}
                                    variant="rounded"
                                />
                                <Box sx={{ flex: 1 }}>
                                    <Typography variant="subtitle1" className="glass-text-primary" sx={{ fontWeight: 600, mb: 0.5 }}>
                                        {product.ProductName}
                                    </Typography>
                                    <Typography variant="body2" className="glass-text-secondary" sx={{ mb: 0.5 }}>
                                        Code: {product.ProductCode}
                                    </Typography>
                                    <Typography variant="h6" sx={{ color: '#bc965e', fontWeight: 700 }}>
                                        ₹{product.Price}
                                    </Typography>
                                </Box>
                            </Box>
                        ))}
                    </Box>
                </DialogContent>
                <DialogActions sx={{ p: 2, borderTop: '1px solid rgba(188, 150, 94, 0.2)' }}>
                    {currentUser && currentUser.role === 'Customer' && !isOwnPost && (
                        <Button
                            onClick={handleAddAllToCart}
                            variant="contained"
                            startIcon={<AddShoppingCartIcon />}
                            sx={{
                                backgroundColor: '#bc965e',
                                '&:hover': {
                                    backgroundColor: '#a67c4a'
                                },
                                mr: 'auto'
                            }}
                        >
                            Add All to Cart
                        </Button>
                    )}
                    <Button
                        onClick={() => setProductsModalOpen(false)}
                        sx={{
                            color: '#bc965e',
                            '&:hover': {
                                backgroundColor: 'rgba(188, 150, 94, 0.1)'
                            }
                        }}
                    >
                        Close
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Add to Cart Dialog with Size/Quantity Selection */}
            <Dialog
                open={addToCartDialogOpen}
                onClose={() => !isAddingToCart && setAddToCartDialogOpen(false)}
                maxWidth="md"
                fullWidth
                PaperProps={{
                    sx: {
                        background: 'rgba(30, 30, 30, 0.95)',
                        backdropFilter: 'blur(10px)',
                        border: '1px solid rgba(188, 150, 94, 0.2)',
                        borderRadius: 2
                    }
                }}
            >
                <DialogTitle sx={{ color: 'white', borderBottom: '1px solid rgba(188, 150, 94, 0.2)' }}>
                    Select Sizes and Quantities
                </DialogTitle>

                <DialogContent sx={{ pt: 3 }}>
                    {content.AttachedProducts && content.AttachedProducts.map((product) => (
                        <Box key={product.ProductCode} sx={{ mb: 4, py: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                                <Avatar
                                    src={product.Photo}
                                    alt={product.ProductName}
                                    sx={{ width: 60, height: 60 }}
                                    variant="rounded"
                                />
                                <Box sx={{ flex: 1 }}>
                                    <Typography variant="subtitle1" className="glass-text-primary" sx={{ fontWeight: 600 }}>
                                        {product.ProductName}
                                    </Typography>
                                    <Typography variant="body2" className="glass-text-secondary">
                                        ₹{product.Price} each
                                    </Typography>
                                </Box>
                                <Button
                                    size="small"
                                    onClick={() => handleAddSizeToProduct(product.ProductCode)}
                                    sx={{
                                        color: '#bc965e',
                                        '&:hover': {
                                            backgroundColor: 'rgba(188, 150, 94, 0.1)'
                                        }
                                    }}
                                >
                                    + Add Size
                                </Button>
                            </Box>

                            {/* Size/Quantity Selectors */}
                            {selectedItems[product.ProductCode]?.map((item, index) => (
                                <Box
                                    key={index}
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1.5,
                                        mb: 2,
                                        ml: { xs: 0, sm: 9 }
                                    }}
                                >
                                    <FormControl size="small" sx={{ flex: '1 1 0', minWidth: 0 }}>
                                        <InputLabel
                                            shrink={!!item.size}
                                            sx={{
                                                color: 'rgba(255, 255, 255, 0.7)',
                                                '&.Mui-focused': { color: '#bc965e' }
                                            }}
                                        >
                                            Size
                                        </InputLabel>
                                        <Select
                                            value={item.size}
                                            onChange={(e) => handleSizeChange(product.ProductCode, index, e.target.value)}
                                            label="Size"
                                            notched={!!item.size}
                                            sx={{
                                                color: 'white',
                                                '.MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(188, 150, 94, 0.3)' },
                                                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(188, 150, 94, 0.5)' },
                                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#bc965e' },
                                                '.MuiSvgIcon-root': { color: 'rgba(255, 255, 255, 0.7)' }
                                            }}
                                            MenuProps={{
                                                PaperProps: {
                                                    sx: { backgroundColor: 'rgba(30, 30, 30, 0.98)', color: 'white' }
                                                }
                                            }}
                                        >
                                            {product.SizeDetails?.filter(s => s.QuantityInStock > 0).map((sizeInfo) => (
                                                <MenuItem key={sizeInfo.Size} value={sizeInfo.Size}>
                                                    {sizeInfo.Size} ({sizeInfo.QuantityInStock} available)
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>

                                    <TextField
                                        type="number"
                                        label="Quantity"
                                        value={item.quantity}
                                        onChange={(e) => {
                                            const selectedSize = item.size;
                                            const sizeInfo = product.SizeDetails?.find(s => s.Size === selectedSize);
                                            const maxStock = sizeInfo?.QuantityInStock || 999;
                                            handleQuantityChange(product.ProductCode, index, e.target.value, maxStock);
                                        }}
                                        size="small"
                                        sx={{
                                            flex: '0 0 90px',
                                            '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.7)' },
                                            '& .MuiOutlinedInput-root': {
                                                color: 'white',
                                                '& fieldset': { borderColor: 'rgba(188, 150, 94, 0.3)' },
                                                '&:hover fieldset': { borderColor: 'rgba(188, 150, 94, 0.5)' },
                                                '&.Mui-focused fieldset': { borderColor: '#bc965e' }
                                            }
                                        }}
                                        InputProps={{
                                            inputProps: {
                                                min: 1,
                                                max: item.size ? product.SizeDetails?.find(s => s.Size === item.size)?.QuantityInStock || 999 : 999
                                            }
                                        }}
                                    />

                                    <IconButton
                                        onClick={() => handleRemoveSizeFromProduct(product.ProductCode, index)}
                                        sx={{ color: '#ff6b6b', flex: '0 0 auto' }}
                                    >
                                        <DeleteIcon />
                                    </IconButton>
                                </Box>
                            ))}
                        </Box>
                    ))}
                </DialogContent>

                <DialogActions sx={{ p: 2, borderTop: '1px solid rgba(188, 150, 94, 0.2)' }}>
                    <Button
                        onClick={() => setAddToCartDialogOpen(false)}
                        disabled={isAddingToCart}
                        sx={{
                            color: '#bc965e',
                            '&:hover': {
                                backgroundColor: 'rgba(188, 150, 94, 0.1)'
                            }
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleAddSelectedToCart}
                        disabled={isAddingToCart}
                        startIcon={<AddShoppingCartIcon />}
                        sx={{
                            backgroundColor: '#bc965e',
                            '&:hover': {
                                backgroundColor: '#a67c4a'
                            }
                        }}
                    >
                        {isAddingToCart ? 'Adding...' : 'Add to Cart'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Comments Modal */}
            <CommentsModal
                open={commentsModalOpen}
                onClose={() => setCommentsModalOpen(false)}
                contentId={content.ContentId}
                onCommentAdded={handleCommentAdded}
            />
        </Card>
    );
};

export default ContentCard;